import type { RequestStatus } from '@domain/vocabulary';

/**
 * The life of a vacation request, as the design draws it.
 *
 * The same model feeds two renderings: the filled segments on Inicio and the
 * vertical stepper on Mis ausencias. Keeping one source for both is what stops
 * them disagreeing about how far along a request is.
 *
 * The verdict step is labelled by outcome — «Aprobada» or «Rechazada» — rather
 * than being a fixed "approved" step that a rejection silently skips. A rejected
 * request did reach a decision; drawing it as though it stalled would misreport
 * what happened.
 */

export type StepState = 'done' | 'current' | 'pending' | 'rejected';

export interface LifecycleStep {
  readonly label: string;
  readonly state: StepState;
}

/**
 * Los pasos posibles, por llave y no por posición.
 *
 * Eran un arreglo de cinco etiquetas y un índice, y eso dejaba de funcionar en
 * cuanto un paso puede no estar: el índice de «Aprobada» es 3 con dos firmas y 2
 * con una. Con llaves, dónde cae cada cosa se calcula sobre la lista que de
 * verdad se va a pintar.
 */
type StepKey = 'enviada' | 'revision' | 'vistoBueno' | 'veredicto' | 'tomada';

/** Dónde se detuvo la historia, y si terminó mal. */
const reached = (status: RequestStatus): { key: StepKey; rejected: boolean } => {
  switch (status) {
    case 'Por revisar':
    case 'En revisión':
      return { key: 'revision', rejected: false };
    case 'Visto bueno':
      return { key: 'vistoBueno', rejected: false };
    case 'Aprobada':
      return { key: 'veredicto', rejected: false };
    case 'Tomada':
      return { key: 'tomada', rejected: false };
    case 'Rechazada':
      return { key: 'veredicto', rejected: true };
    case 'Cancelada':
      return { key: 'revision', rejected: true };
    default:
      // An unknown status degrades to "sent and nothing else" rather than
      // throwing — the OTA rule applies to progress bars too.
      return { key: 'enviada', rejected: false };
  }
};

/**
 * Las fases de una solicitud, para quien mira la suya.
 *
 * **`requiredSignatures` decide si existe el paso del segundo líder**, y por eso
 * es obligatorio y no tiene valor por omisión: un `2` de respaldo habría dejado
 * los llamadores viejos enseñando el paso de más sin que nada protestara, que es
 * justo lo que se viene a arreglar.
 *
 * Hasta ahora la tarjeta dibujaba «Visto bueno 2º líder» siempre. A quien tiene
 * un solo líder —17 de las 32 solicitudes de producción— le prometía un trámite
 * que no va a ocurrir nunca: su solicitud salta de «Revisión del líder» a
 * «Aprobada» y ese punto se queda gris para siempre, indistinguible de un paso
 * atorado. El dato ya estaba en la solicitud; lo que faltaba era mirarlo.
 *
 * Se lee de la solicitud y no del número de líderes que la persona tenga **hoy**:
 * `requiredSignatures` se congela al enviar, así que una solicitud en vuelo no
 * cambia de forma porque a su dueño le asignen o le quiten un líder por el
 * camino. Es la misma razón por la que el servidor lo congeló.
 */
export const lifecycleSteps = (
  status: RequestStatus,
  requiredSignatures: 1 | 2,
): readonly LifecycleStep[] => {
  const { key, rejected } = reached(status);

  const keys: readonly StepKey[] =
    requiredSignatures >= 2
      ? ['enviada', 'revision', 'vistoBueno', 'veredicto', 'tomada']
      : ['enviada', 'revision', 'veredicto', 'tomada'];

  const label = (step: StepKey): string => {
    switch (step) {
      case 'enviada':
        return 'Enviada';
      case 'revision':
        return 'Revisión del líder';
      case 'vistoBueno':
        return 'Visto bueno 2º líder';
      case 'veredicto':
        return rejected && status === 'Rechazada' ? 'Rechazada' : 'Aprobada';
      case 'tomada':
        return 'Tomada';
    }
  };

  /*
   * Si el paso alcanzado no está en la lista, se cae al de revisión.
   *
   * Sólo pasa con un dato incoherente —«Visto bueno» sobre una solicitud de una
   * firma—, que hoy no existe en producción: están las 32 contadas y ninguna lo
   * tiene. Pero un `indexOf` que devuelve `-1` pintaría **todo** el recorrido
   * como pendiente, incluido «Enviada», y eso se ve como una solicitud que nunca
   * salió. Caer en el paso anterior dice algo menos preciso pero cierto.
   */
  const alcanzado = keys.indexOf(key);
  const index = alcanzado === -1 ? keys.indexOf('revision') : alcanzado;

  const labels = keys.map(label);

  /*
   * Una cancelación se marca donde ocurrió, no en la casilla del veredicto.
   *
   * «Cancelada» vivía fija en la cuarta posición, la del sí o el no del líder, y
   * una cancelación se detiene en la segunda — así que la etiqueta caía **después**
   * del punto alcanzado y se pintaba como paso pendiente: gris, apagada, como si
   * no hubiera pasado nada. QA lo reportó como que el cancelado no se ve.
   *
   * Cancelar no es un veredicto ajeno: es el solicitante retirando lo suyo, y
   * puede ocurrir en cualquier punto anterior a la decisión. Por eso la etiqueta
   * va donde se detuvo la historia.
   */
  if (status === 'Cancelada') labels[index] = 'Cancelada';

  return labels.map((texto, position) => {
    if (position > index) return { label: texto, state: 'pending' as const };
    if (position === index) {
      return { label: texto, state: rejected ? ('rejected' as const) : ('current' as const) };
    }
    /*
     * Lo anterior al final se pinta cumplido aunque el final fuera malo.
     *
     * Se pintaba en rojo hasta el punto alcanzado, así que una solicitud
     * rechazada mostraba «Enviada» en rojo — y enviarla no falló. Lo que salió
     * mal es el paso donde se detuvo, y es el único que debe decirlo.
     */
    return { label: texto, state: 'done' as const };
  });
};
